import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton"; // 1. Import IconButton
import Visibility from "@mui/icons-material/Visibility"; // 1. Import Visibility icon
import VisibilityOff from "@mui/icons-material/VisibilityOff"; // 1. Import VisibilityOff icon
import { useNavigate } from "react-router-dom";
import { useSession } from "../Services/AuthContext";
import { ApiManager } from "../Services/Networking";
import { useState } from "react";
import MuiCard from "@mui/material/Card";
import { styled } from "@mui/material/styles";
import LogoLight from "../theme/assets/HRMLogo.jpeg";

const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: "auto",
  [theme.breakpoints.up("sm")]: {
    maxWidth: "450px",
  },
  boxShadow:
    "hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px",
  ...theme.applyStyles("dark", {
    boxShadow:
      "hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px",
  }),
}));

const SignInContainer = styled(Stack)(({ theme }) => ({
  height: "calc((1 - var(--template-frame-height, 0)) * 100dvh)",
  minHeight: "100%",
  minWidth: "100%",
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
  "&::before": {
    content: '""',
    display: "block",
    position: "absolute",
    zIndex: -1,
    inset: 0,
    backgroundImage:
      "radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))",
    backgroundRepeat: "no-repeat",
    ...theme.applyStyles("dark", {
      backgroundImage:
        "radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))",
    }),
  },
}));

export default function SignIn() {
  const { signIn } = useSession();
  const navigate = useNavigate();
  const [number, setNumber] = useState("9843888600");
  const [password, setPassword] = useState("Secret@123");
  const [numberError, setNumberError] = useState(false);
  const [numberErrorMessage, setNumberErrorMessage] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false); // 3. State for visibility

  const handleClickShowPassword = () => {
    setShowPassword((show) => !show);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const validateInputs = () => {
    let isValid = true;
    const numberRegex = /^\d{10}$/;
    if (!number || !numberRegex.test(number)) {
      setNumberError(true);
      setNumberErrorMessage("Please enter a valid 10-digit phone number.");
      isValid = false;
    } else {
      setNumberError(false);
      setNumberErrorMessage("");
    }
    if (!password || password.length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage("Password must be at least 6 characters long.");
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage("");
    }
    return isValid;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateInputs()) {
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await ApiManager.signIn({
        phone: `+91${number}`,
        password,
      });
      if (response && response.success) {
        signIn(response.data.user);
        navigate("/home");
      } else {
        setPasswordError(true);
        setPasswordErrorMessage("Login failed. Check your credentials.");
      }
    } catch (error) {
      setPasswordError(true);
      setPasswordErrorMessage("A connection error occurred. Try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNumberChange = (e) => {
    const value = e.target.value;
    // 1. Filter out non-digit characters
    const numericValue = value.replace(/[^0-9]/g, "");

    const limitedValue = numericValue.slice(0, 10);

    setNumber(limitedValue);

    // 3. Error Handling
    if (value !== limitedValue) {
      if (numericValue.length > 10) {
        setNumberError(true);
        setNumberErrorMessage(`Must be ${10} digits.`);
      } else if (value.length > numericValue.length) {
        setNumberError(true);
        setNumberErrorMessage("Only numbers are allowed.");
      } else {
        // Clear error if input is valid based on rules
        setNumberError(false);
        setNumberErrorMessage("");
      }
    } else {
      setNumberError(false);
      setNumberErrorMessage("");
    }
  };

  return (
    <SignInContainer
      direction="column"
      justifyContent="center"
      alignItems="center"
    >
      <Box>
        <Box
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "48px",
          }}
        >
          <img
            src={LogoLight}
            style={{ width: "128px", aspectRatio: 1, borderRadius: "16px" }}
          />
        </Box>
        <Card variant="outlined">
          <Typography
            component="h1"
            variant="h4"
            sx={{
              minWidth: "300px",
              width: "100%",
              fontSize: "clamp(2rem, 10vw, 2.15rem)",
            }}
          >
            Sign in
          </Typography>
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              gap: 2,
            }}
          >
            <FormControl>
              <FormLabel htmlFor="number">Phone Number</FormLabel>
              <TextField
                error={numberError}
                helperText={numberErrorMessage}
                id="number"
                name="number"
                placeholder="Enter your Phone Number"
                autoComplete="tel"
                autoFocus
                required
                fullWidth
                variant="outlined"
                color={numberError ? "error" : "primary"}
                value={number}
                onChange={handleNumberChange}
                disabled={isSubmitting}
                inputProps={{
                  // Set maxLength for native HTML behavior (optional, as JS handles it)
                  maxLength: 10,
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
              />
            </FormControl>
            <FormControl>
              <FormLabel htmlFor="password">Password</FormLabel>
              <TextField
                error={passwordError}
                helperText={passwordErrorMessage}
                name="password"
                placeholder="••••••"
                id="password"
                autoComplete="current-password"
                required
                fullWidth
                variant="outlined"
                color={passwordError ? "error" : "primary"}
                value={password}
                type={showPassword ? "text" : "password"}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSubmitting}
                InputProps={{
                  // 4. Add InputProps for adornment
                  endAdornment: (
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
              />
            </FormControl>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Signing In..." : "Sign in"}
            </Button>
          </Box>
        </Card>
      </Box>
    </SignInContainer>
  );
}
